/**
 * AGI Auth Store - Separate authentication for @agi.live team
 * Uses email/password instead of wallet
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import toast from 'react-hot-toast'

// Create axios instance for AGI API calls
const agiApi = axios.create({
  baseURL: API_BASE_URL
})

export interface AgiUser {
  id: number
  email: string
  displayName: string
  credits: number
  isAgi: boolean      // Is @agi.live email
  isAdmin: boolean    // Can resolve markets
  stats?: {
    total_bets: number
    total_wagered: number
    total_won: number
  }
}

interface AgiAuthState {
  agiUser: AgiUser | null
  agiToken: string | null
  isAgiLoading: boolean
  isAgiAuthenticated: boolean
  
  // Actions
  loginWithEmail: (email: string, password: string, displayName?: string) => Promise<boolean>
  logoutAgi: () => void
  refreshAgiUser: () => Promise<void>
  updateCredits: (newCredits: number) => void
}

export const useAgiAuthStore = create<AgiAuthState>()(
  persist(
    (set, get) => ({
      agiUser: null,
      agiToken: null,
      isAgiLoading: false,
      isAgiAuthenticated: false,

      loginWithEmail: async (email: string, password: string, displayName?: string) => {
        try {
          set({ isAgiLoading: true })
          
          console.log('🔐 AGI Login attempt:', email)
          
          const response = await agiApi.post('/api/agi/auth', {
            email,
            password,
            displayName
          })

          const { token, user } = response.data
          console.log('✅ AGI Login successful:', user.email, 'Credits:', user.credits)

          // Set axios header for AGI requests
          agiApi.defaults.headers.common['Authorization'] = `Bearer ${token}`

          set({
            agiUser: user,
            agiToken: token,
            isAgiAuthenticated: true,
            isAgiLoading: false
          })

          if (user.isAgi) {
            toast.success(`Welcome to AGI Markets! 🎯 You have ${user.credits} credits`)
          } else {
            toast.success(`Logged in, but AGI Markets requires @agi.live email`)
          }
          
          return true
          
        } catch (error: any) {
          console.error('❌ AGI Login error:', error)
          toast.error(error.response?.data?.error || 'Login failed')
          set({ isAgiLoading: false })
          return false
        }
      },

      logoutAgi: () => {
        delete agiApi.defaults.headers.common['Authorization']
        
        set({
          agiUser: null,
          agiToken: null,
          isAgiAuthenticated: false,
          isAgiLoading: false
        })

        toast.success('Logged out of AGI Markets')
      },

      refreshAgiUser: async () => {
        try {
          const { agiToken } = get()
          if (!agiToken) {
            set({ isAgiLoading: false })
            return
          }

          agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
          
          const response = await agiApi.get('/api/agi/me')
          
          set({
            agiUser: response.data,
            isAgiAuthenticated: true,
            isAgiLoading: false
          })
          
        } catch (error: any) {
          console.error('AGI refresh error:', error)
          
          if (error.response?.status === 401) {
            get().logoutAgi()
          } else {
            set({ isAgiLoading: false })
          }
        }
      },

      updateCredits: (newCredits: number) => {
        set(state => ({
          agiUser: state.agiUser ? { ...state.agiUser, credits: newCredits } : null
        }))
      }
    }),
    {
      name: 'agi-auth-storage',
      onRehydrateStorage: () => (state) => {
        if (state?.agiToken) {
          agiApi.defaults.headers.common['Authorization'] = `Bearer ${state.agiToken}`
          state.refreshAgiUser()
        }
      }
    }
  )
)

// Export the API instance for use in other components
export { agiApi }
